import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, MessageSquareText, Eye, History, Check, Search, Network, Palette, Code2, ShieldCheck, Rocket, Clock, Lock, Coins } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, EXAMPLES, WEBSITE_EXAMPLES, PENDING_KEY, startProject, fmtMoney, fmtDate, formatError } from "@/lib/api";
import { AppPreview } from "@/components/AppPreview";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

function Nav() {
  const { user } = useAuth();
  return (
    <header className="fixed top-0 inset-x-0 z-40 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="flex items-center gap-2 sm:gap-6 text-sm">
          {[["#demo", "Demostración"], ["#como-funciona", "Cómo funciona"], ["#precios", "Precios"], ["#faq", "Preguntas"]].map(([h, l]) => (
            <a key={h} href={h} className="hidden md:block text-slate-400 hover:text-white transition-colors" data-testid={`nav-${h.slice(1)}-link`}>{l}</a>
          ))}
          {user ? (
            <Link to="/app" data-testid="nav-dashboard-btn" className="px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium transition-colors">Mi panel</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="text-slate-300 hover:text-white transition-colors">Iniciar sesión</Link>
              <Link to="/registro" data-testid="nav-register-btn" className="px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium transition-colors">Crear cuenta</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function PromptBox() {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState("app");
  const { user } = useAuth();
  const navigate = useNavigate();
  const submit = async () => {
    const text = prompt.trim();
    if (!text) return toast.error("Describe lo que quieres crear");
    if (!user) { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ text, type })); return navigate("/registro"); }
    setBusy(true);
    try { const p = await startProject(text, undefined, type); navigate(`/app/proyecto/${p.id}`, { state: { initialPrompt: text } }); }
    catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  return (
    <div className="rise w-full max-w-3xl" style={{ animationDelay: "240ms" }}>
      <div className="flex items-center justify-between mb-3">
        <label htmlFor="hero-prompt" className="text-xs uppercase tracking-[0.22em] font-semibold text-cyan-400">{type === "website" ? "¿Qué página web quieres crear?" : "¿Qué aplicación quieres crear hoy?"}</label>
        <div className="flex rounded-full border border-[#222B40] p-0.5 text-xs" data-testid="hero-type-toggle">
          {[["app", "Aplicación"], ["website", "Página web"]].map(([v, l]) => <button key={v} onClick={() => setType(v)} data-testid={`hero-type-${v}`} className={`px-3 py-1 rounded-full ${type === v ? "bg-blue-500 text-white" : "text-slate-400"}`}>{l}</button>)}
        </div>
      </div>
      <div className="relative rounded-2xl border border-blue-500/30 bg-[#111622]/90 shadow-[0_0_60px_-15px_rgba(59,130,246,0.5)] focus-within:border-cyan-400/60 transition-colors">
        <textarea id="hero-prompt" data-testid="hero-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder="Ej: Un sistema de inventarios para mi ferretería con productos, entradas, salidas y alertas de stock bajo…"
          className="w-full resize-none bg-transparent px-5 pt-5 pb-16 text-base text-slate-100 placeholder:text-slate-500 outline-none" />
        <button onClick={submit} disabled={busy} data-testid="hero-prompt-submit-btn"
          className="absolute right-3 bottom-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-semibold text-sm transition-[background-color,transform] active:scale-95">
          <Sparkles className="w-4 h-4" /> {busy ? "Creando…" : "Crear aplicación"}
        </button>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" data-testid="example-chips">
        {(type === "website" ? WEBSITE_EXAMPLES : EXAMPLES).map((ex, i) => (
          <button key={ex} data-testid={`prompt-chip-${i}`} onClick={() => setPrompt(type === "website" ? `Quiero un ${ex.toLowerCase()} con inicio, servicios, galería, testimonios y formulario de contacto.` : `Quiero un ${ex.toLowerCase()} para mi negocio.`)}
            className="px-3.5 py-1.5 rounded-full border border-[#222B40] bg-[#111622] text-slate-300 text-sm hover:border-cyan-400/60 hover:text-cyan-200 transition-colors">{ex}</button>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-36 pb-24 overflow-hidden noise">
      <div className="absolute inset-0 grid-bg [mask-image:linear-gradient(to_bottom,black,transparent)]" />
      <div className="absolute inset-0 glow-top" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 flex flex-col items-start">
        <span className="rise inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-medium mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> Plataforma no-code con 6 agentes de IA
        </span>
        <h1 className="rise max-w-5xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]" style={{ animationDelay: "80ms" }} data-testid="hero-title">
          CREA TU PROPIO SOFTWARE <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">SIN SABER PROGRAMAR</span>
        </h1>
        <p className="rise mt-6 max-w-2xl text-base md:text-lg text-slate-400 leading-relaxed" style={{ animationDelay: "160ms" }}>
          Describe tu idea y nuestros agentes de inteligencia artificial te ayudarán a construirla.
        </p>
        <div className="mt-12 w-full"><PromptBox /></div>
      </div>
    </section>
  );
}

const BENEFITS = [
  { icon: MessageSquareText, t: "Hablas, no programas", d: "Explica lo que necesitas con tus palabras; si falta algo, el analista te hace preguntas sencillas." },
  { icon: ShieldCheck, t: "Verificado antes de mostrarse", d: "Cada versión pasa por comprobaciones automáticas de sintaxis, formularios, navegación y seguridad básica." },
  { icon: Lock, t: "Aislado y privado", d: "Tus proyectos son solo tuyos y las apps se ejecutan en un entorno aislado, sin acceso a la plataforma." },
  { icon: History, t: "Cambios sin miedo", d: "Cada cambio crea una versión. Puedes volver a cualquier versión anterior con un clic." },
  { icon: Coins, t: "Costos transparentes", d: "Ves el costo estimado antes de cada operación y nunca se cobra más de lo que autorizas." },
  { icon: Rocket, t: "Publica en minutos", d: "Obtén una dirección web para compartir tu aplicación y publica nuevas versiones cuando quieras." },
];

const Section = ({ id, eyebrow, title, children, alt }) => (
  <section id={id} className={`py-24 border-t border-white/5 ${alt ? "bg-[#0c1019]" : ""}`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <p className="text-xs uppercase tracking-[0.22em] font-semibold text-cyan-400">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-semibold text-white max-w-2xl">{title}</h2>
      <div className="mt-12">{children}</div>
    </div>
  </section>
);

function Benefits() {
  return (
    <Section id="beneficios" eyebrow="Beneficios" title="Software a la medida de tu negocio, sin depender de programadores">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {BENEFITS.map((b, i) => (
          <div key={b.t} className="p-6 rounded-2xl border border-[#222B40] bg-[#111622] hover:border-blue-500/40 transition-colors" data-testid={`benefit-${i}`}>
            <b.icon className="w-6 h-6 text-blue-400" /><h3 className="mt-4 text-lg font-medium text-white">{b.t}</h3><p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Examples() {
  return (
    <Section eyebrow="Ejemplos" title="¿Qué puedes crear?" alt>
      <div className="flex flex-wrap gap-3">
        {EXAMPLES.map((e) => <span key={e} className="px-4 py-2 rounded-xl border border-[#222B40] bg-[#111622] text-slate-200 text-sm">{e}</span>)}
      </div>
      <h3 className="mt-10 text-white font-medium">Chatbots con IA para WhatsApp</h3>
      <p className="mt-1 text-sm text-slate-400 max-w-2xl">Entrena un asistente con la información de tu comercio (productos, precios, horarios), pruébalo en el simulador y conéctalo a tu número de WhatsApp Business para atender clientes 24/7.</p>
      <h3 className="mt-10 text-white font-medium">Y también páginas web completas</h3>
      <p className="mt-1 text-sm text-slate-400 max-w-2xl">Varias secciones o páginas, diseño responsivo, textos e imágenes, etiquetas SEO, galería, testimonios, preguntas frecuentes, botón de WhatsApp y formularios de contacto cuyos mensajes recibes en tu panel.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        {WEBSITE_EXAMPLES.map((e) => <span key={e} className="px-4 py-2 rounded-xl border border-emerald-500/20 bg-[#111622] text-slate-200 text-sm">{e}</span>)}
      </div>
      <p className="mt-6 text-sm text-slate-500 max-w-2xl">Apps web de complejidad baja y media: los datos se guardan en el navegador de quien las usa. Las apps con servidor propio y base de datos en la nube llegarán en un próximo módulo.</p>
    </Section>
  );
}

const AGENTS = [[Search, "Analista", "Entiende tu idea y detecta lo que falta"], [Network, "Arquitecto", "Define pantallas, datos y permisos"], [Palette, "Diseñador", "Crea una interfaz moderna y accesible"],
  [Code2, "Programador", "Escribe los archivos reales de la app"], [ShieldCheck, "Verificador", "Prueba y pide correcciones (intentos limitados)"], [Rocket, "Publicador", "Compila y publica con confirmación"]];

function HowItWorks() {
  return (
    <Section id="como-funciona" eyebrow="Cómo funciona" title="Un coordinador central dirige a seis agentes especializados">
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {AGENTS.map(([Icon, n, d], i) => (
          <div key={n} className="p-5 rounded-2xl border border-[#222B40] bg-[#111622]" data-testid={`agent-step-${i}`}>
            <span className="font-mono text-xs text-slate-500">0{i + 1}</span><Icon className="w-5 h-5 text-emerald-400 mt-3" />
            <h3 className="mt-3 text-white font-medium">{n}</h3><p className="mt-1 text-xs text-slate-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Demo() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/demo").then((r) => setD(r.data)).catch(() => setD({ available: false })); }, []);
  if (!d) return null;
  return (
    <Section id="demo" eyebrow="Demostración" title="Así se convirtió una descripción en un sistema de inventarios" alt>
      {!d.available ? <p className="text-slate-400 text-sm" data-testid="demo-unavailable">La demostración aún no está disponible.</p> : (
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6" data-testid="demo-section">
          <div className="space-y-4">
            <p className="inline-flex items-center gap-2 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-full px-3 py-1" data-testid="demo-recorded-label">
              <Clock className="w-3.5 h-3.5" />Grabación de una ejecución real · {fmtDate(d.recorded_at)} · no se ejecuta en vivo
            </p>
            <div className="rounded-2xl border border-[#222B40] bg-[#111622] p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500">1 · Descripción del usuario</p>
              <p className="mt-2 text-sm text-slate-200" data-testid="demo-prompt">“{d.prompt}”</p>
            </div>
            <div className="rounded-2xl border border-[#222B40] bg-[#111622] p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500">2 · Agentes que trabajaron ({d.model})</p>
              <div className="flex flex-wrap gap-2 mt-3">{[...new Set(d.agents_run)].map((a) => <span key={a} className="text-xs px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-300">{{ analyst: "Analista", architect: "Arquitecto", designer: "Diseñador", programmer: "Programador", verifier: "Verificador" }[a] || a}</span>)}</div>
              {d.analysis?.pantallas && <p className="mt-3 text-xs text-slate-400">Pantallas detectadas: {d.analysis.pantallas.join(", ")}</p>}
            </div>
            <div className="rounded-2xl border border-[#222B40] bg-[#111622] p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500">3 · Archivos generados y verificación</p>
              <p className="mt-2 font-mono text-xs text-slate-300">{Object.entries(d.files).map(([f, n]) => `${f} (${(n / 1024).toFixed(1)} KB)`).join(" · ")}</p>
              {d.quality && <p className={`mt-2 text-sm ${d.quality.passed ? "text-emerald-300" : "text-amber-300"}`} data-testid="demo-quality">{d.quality.passed ? "Verificación superada" : "Verificación con observaciones"}: {d.quality.checks.filter((c) => c.ok).length}/{d.quality.checks.length} comprobaciones</p>}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#222B40] h-[560px]">
            <div className="h-9 flex items-center px-3 text-[11px] text-slate-400 bg-[#0c1019] border-b border-white/5">4 · Resultado: la aplicación generada (v1), interactiva en un entorno aislado</div>
            <div className="h-[calc(100%-2.25rem)]"><AppPreview html={d.html} dataKey="landing-demo" testId="demo-iframe" /></div>
          </div>
        </div>
      )}
    </Section>
  );
}

function Pricing() {
  const [cat, setCat] = useState(null);
  useEffect(() => { api.get("/billing/catalog").then((r) => setCat(r.data)).catch(() => {}); }, []);
  if (!cat) return null;
  return (
    <Section id="precios" eyebrow="Planes y precios" title="Elige el plan para tu etapa">
      <div className="grid md:grid-cols-3 gap-4">
        {cat.plans.map((p, i) => (
          <div key={p.id} className={`p-7 rounded-2xl border ${i === 1 ? "border-blue-500/60 shadow-[0_0_40px_-12px_rgba(59,130,246,0.6)]" : "border-[#222B40]"} bg-[#111622]`} data-testid={`landing-plan-${p.id}`}>
            <h3 className="text-white font-medium">{p.name}</h3>
            <p className="mt-3 text-4xl font-display font-bold text-white">{fmtMoney(p.price, cat.currency)}<span className="text-sm text-slate-500 font-normal">/mes</span></p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />{f}</li>)}</ul>
            <Link to="/registro" className={`mt-8 block text-center py-2.5 rounded-xl text-sm font-semibold ${i === 1 ? "bg-blue-500 hover:bg-blue-400 text-white" : "border border-[#222B40] text-slate-200 hover:border-blue-500/40"}`} data-testid={`landing-plan-cta-${p.id}`}>Comenzar</Link>
          </div>
        ))}
      </div>
      <p className="mt-6 text-sm text-slate-500">¿Necesitas más? Paquetes adicionales desde {fmtMoney(Math.min(...cat.packs.map((p) => p.price)), cat.currency)}. Precios en dólares estadounidenses, provisionales. Sin créditos gratuitos.</p>
    </Section>
  );
}

const FAQ = [
  ["¿Necesito saber programar?", "No. Describes lo que necesitas en español y los agentes construyen la aplicación. Si falta información, te harán preguntas sencillas."],
  ["¿Qué tipo de aplicaciones puedo crear?", "Aplicaciones web de complejidad baja y media: inventarios, puntos de venta, agendas, clientes, gastos, cotizaciones, pedidos, cursos y formularios. Los datos se guardan en el navegador de quien usa la app."],
  ["¿Cómo funcionan los créditos?", "Cada operación de los agentes consume créditos según el trabajo real (modelo de IA, herramientas, almacenamiento). Antes de enviar ves el costo estimado y autorizas un máximo; si algo falla, se devuelven los créditos."],
  ["¿Puedo modificar mi aplicación después?", "Sí. Pide cambios en el chat (\"cambia el color del menú\", \"agrega un reporte de ventas\") y se crea una nueva versión verificada. Puedes restaurar cualquier versión anterior."],
  ["¿Cómo publico mi aplicación?", "Desde la pestaña Publicar: eliges la versión, ejecutas las pruebas y obtienes una dirección web. Solo se marca como publicada cuando el proveedor lo confirma."],
  ["¿Mis datos están seguros?", "Cada usuario tiene su propio espacio aislado. Las apps generadas se ejecutan en un entorno aislado sin acceso a internet ni a la plataforma."],
  ["¿Puedo cancelar mi plan?", "Sí, en cualquier momento desde Plan contratado. Conservas el plan hasta el final del periodo pagado."],
];

function Faq() {
  return (
    <Section id="faq" eyebrow="Preguntas frecuentes" title="Resolvemos tus dudas" alt>
      <Accordion type="single" collapsible className="max-w-3xl">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`} className="border-[#222B40]">
            <AccordionTrigger className="text-left text-slate-100 hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-slate-400">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0D14]" data-testid="landing-page">
      <Nav /><Hero /><Benefits /><Demo /><HowItWorks /><Examples /><Pricing /><Faq />
      <section className="py-24 border-t border-white/5 text-center">
        <h2 className="text-3xl font-semibold text-white">Tu próxima herramienta de trabajo empieza con una frase</h2>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/registro" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-semibold" data-testid="final-register-btn">Crear mi cuenta <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/login" className="px-6 py-3 rounded-full border border-[#222B40] text-slate-200" data-testid="final-login-btn">Iniciar sesión</Link>
        </div>
      </section>
      <footer className="py-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row justify-between gap-4 text-sm text-slate-500">
          <Logo /><span>© {new Date().getFullYear()} SYNAPSIS · Crea tu propio software sin saber programar</span>
        </div>
      </footer>
    </div>
  );
}
